import { useNavigate, NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  LifeBuoy,
  Puzzle,
  Zap,
  Cpu,
  Cloud,
  Rocket,
  HeartPulse,
  Lock,
  Settings,
  ArrowDownToLine,
  Coins,
  ArrowLeft,
  Sparkles,
  LogOut,
  User,
} from "lucide-react";
import { NavSection, type NavItem } from "./nav-section";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/context/auth-context";

const operateItems: NavItem[] = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "Tenants", to: "/tenants", icon: Building2 },
  { label: "Users", to: "/users", icon: Users },
  { label: "Support Tickets", to: "/support-tickets", icon: LifeBuoy },
  { label: "LLM Usage", to: "/llm-usage", icon: Coins },
];

const catalogueItems: NavItem[] = [
  { label: "MCP Catalogue", to: "/mcp-catalogue", icon: Puzzle },
  { label: "Skill Catalogue", to: "/skill-catalogue", icon: Zap },
  { label: "Model Catalogue", to: "/models", icon: Cpu },
];

const infraItems: NavItem[] = [
  { label: "Azure", to: "/azure", icon: Cloud },
  { label: "Deployments", to: "/deployments", icon: Rocket },
  { label: "Agent Health", to: "/agent-health", icon: HeartPulse },
  { label: "Agent Updates", to: "/agent-updates", icon: ArrowDownToLine },
  { label: "Vault", to: "/vault", icon: Lock },
];

const platformAdminItems: NavItem[] = [
  { label: "Platform Settings", to: "/settings", icon: Settings },
];

export function AdminSidebar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  // If the user reached the admin shell from a tenant (via the user-menu
  // "Platform admin" action), we stashed that path in sessionStorage so we
  // can offer a one-click return.
  const backToAppPath =
    typeof window !== "undefined"
      ? sessionStorage.getItem("proclaw-admin-origin-path")
      : null;

  const handleBackToApp = () => {
    sessionStorage.removeItem("proclaw-admin-origin-path");
    if (backToAppPath) navigate(backToAppPath);
  };

  return (
    <aside className="flex h-full w-[260px] min-w-[260px] shrink-0 flex-col border-r border-border bg-sidebar">
      {/* Logo */}
      <div className="flex h-14 items-center gap-2 px-5">
        <Sparkles size={20} className="text-accent" />
        <span className="text-[20px] font-semibold text-text-primary">ProClaw</span>
        <span className="ml-auto rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent">
          MSP
        </span>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {backToAppPath && (
          <button
            onClick={handleBackToApp}
            className="mb-2 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium text-text-primary transition-colors hover:bg-surface-hover"
          >
            <ArrowLeft size={14} className="text-text-secondary" />
            Back to app
          </button>
        )}
        <NavSection label="Operate" items={operateItems} />
        <NavSection label="Catalogues" items={catalogueItems} />
        <NavSection label="Infrastructure" items={infraItems} />
        <div className="my-2 h-px bg-border" />
        <NavSection label="Platform Admin" items={platformAdminItems} />
      </div>

      {/* Footer */}
      <div className="border-t border-border px-4 py-3">
        {user && (
          <NavLink
            to="/account"
            className="mb-2 flex items-center gap-2 rounded-md px-1 py-1 text-[11px] text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
            title="My account"
          >
            <User size={14} />
            <span className="truncate flex-1">{user.email}</span>
          </NavLink>
        )}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-text-muted">v0.1.0</span>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button
              onClick={handleSignOut}
              className="text-text-muted hover:text-text-primary transition-colors p-1"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
